import { type ClipboardEvent, useCallback, useRef, useState, type MutableRefObject } from 'react';
import {
  CSS_CLASSES,
  EDITOR_DATA_ATTR,
  INITIAL_CONTEXT_MENU_STATE,
  MENTION_TAG_NAME,
  MENTION_TEXT,
  VARIABLE_TAG_NAME,
} from '../constants';
import { extractTextFromEditor, buildEditorContent } from '../tiptapEditorUtils';
import { useTiptapEditorKeyboard } from './useTiptapEditorKeyboard';
import type { Editor } from '@tiptap/react';
import type { AvailableInput, PromptEditorContextMenuState } from '../types';
import type { PromptDisplayMode } from 'web';

interface UseTiptapEditorHelpersParams {
  editorRef: MutableRefObject<Editor | null>;
  lastValueRef: MutableRefObject<string>;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
  hasEditingPermissions: boolean;
  onChange: (prompt: string) => void;
  onPaste: (evt: ClipboardEvent<HTMLDivElement>) => void;
  onCreatePrompt: (queryText: string) => { label: string; key: string } | undefined;
}

export const useTiptapEditorHelpers = ({
  editorRef,
  lastValueRef,
  availableInputs,
  displayMode,
  hasEditingPermissions,
  onChange,
  onPaste,
  onCreatePrompt,
}: UseTiptapEditorHelpersParams) => {
  const [contextMenu, setContextMenu] = useState<PromptEditorContextMenuState>(INITIAL_CONTEXT_MENU_STATE);
  const highlightedTagRef = useRef<Element | null>(null);
  // Wrapper for setContextMenu that clears tag highlight when closing and keeps ref in sync
  const handleSetContextMenu = useCallback(
    (
      newState: PromptEditorContextMenuState | ((prev: PromptEditorContextMenuState) => PromptEditorContextMenuState),
    ) => {
      setContextMenu((prev) => {
        const nextState = typeof newState === 'function' ? newState(prev) : newState;

        if (nextState.type === 'closed' && highlightedTagRef.current) {
          highlightedTagRef.current.classList.remove(CSS_CLASSES.TAG_CONTEXT_MENU_OPEN);
          highlightedTagRef.current = null;
        }

        return nextState;
      });
    },
    [highlightedTagRef],
  );

  /**
   * Handles detection of "@" key press to show the mention menu.
   * Inserts a mention trigger tag and shows the menu at the cursor position.
   */
  const handleMentionMenuDetection = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!editorRef.current) return false;

      // Detect "@" key press to show mention menu
      if (event.key === MENTION_TEXT && contextMenu.type === 'closed') {
        // Prevent the default "@" insertion
        event.preventDefault();
        event.stopPropagation();

        // Get position BEFORE inserting the tag (at the beginning of where @ will be)
        const coords = editorRef.current.view.coordsAtPos(editorRef.current.state.selection.from);

        // Insert "@" as an inlineTag directly
        editorRef.current
          .chain()
          .focus()
          .insertContent({
            type: VARIABLE_TAG_NAME,
            attrs: {
              tagKey: MENTION_TAG_NAME,
            },
          })
          .run();

        // Show menu at the position where @ starts
        handleSetContextMenu({
          type: 'mention',
          x: coords.left,
          y: coords.bottom + 4,
          queryText: '',
        });

        return true;
      }

      return false;
    },
    [editorRef, contextMenu, handleSetContextMenu],
  );

  const { handleEditorKeyDown, handleEditorDOMKeyDown } = useTiptapEditorKeyboard({
    editorRef,
    availableInputs,
    displayMode,
    contextMenu,
    handleSetContextMenu,
    handleMentionMenuDetection,
    onCreatePrompt,
  });

  const handleEditorPaste = useCallback(
    (event: globalThis.ClipboardEvent) => {
      // Forward paste event for validation (truncation warning)
      const syntheticEvent = {
        ...event,
        clipboardData: event.clipboardData,
        preventDefault: () => event.preventDefault(),
      } as unknown as ClipboardEvent<HTMLDivElement>;
      onPaste(syntheticEvent);

      // Check plain text for {{tagKey:tagValue}} patterns and convert matching tags
      const plainText = event.clipboardData?.getData('text/plain') ?? '';

      if (plainText.includes('{{') && editorRef.current) {
        const pasteContent = buildEditorContent(plainText, availableInputs, displayMode);
        event.preventDefault();

        // Single paragraph: insert only the inline content to avoid creating a new line
        const content = pasteContent.length === 1 && pasteContent[0].content ? pasteContent[0].content : pasteContent;

        editorRef.current.chain().focus().insertContent(content).run();
        return true; // We handled the paste
      }

      return false; // Let default handling occur
    },
    [onPaste, availableInputs, displayMode, editorRef],
  );

  /**
   * Handles right-click context menu on tag elements.
   * Returns true if a tag context menu was opened, false otherwise.
   */
  const handleTagContextMenu = useCallback(
    (event: MouseEvent, view: { posAtDOM: (element: Element, offset: number) => number }): boolean => {
      const target = event.target as HTMLElement;
      const tagElement = target.closest(`[${EDITOR_DATA_ATTR.TAG_KEY}]`);

      if (tagElement) {
        const tagKey = tagElement.getAttribute(EDITOR_DATA_ATTR.TAG_KEY);
        // Don't show context menu for mention trigger
        if (tagKey && tagKey !== MENTION_TAG_NAME) {
          // Get the position of the clicked tag element directly from DOM
          // This correctly handles multiple tags with the same key
          const tagPos = view.posAtDOM(tagElement, 0);

          // Add highlight class to the tag
          tagElement.classList.add(CSS_CLASSES.TAG_CONTEXT_MENU_OPEN);
          highlightedTagRef.current = tagElement;

          handleSetContextMenu({
            type: 'tag',
            x: event.clientX,
            y: event.clientY,
            tagKey,
            tagPos,
          });

          return true; // Event handled
        }
      }

      return false;
    },
    [handleSetContextMenu, highlightedTagRef],
  );

  const handleEditorContextMenu = useCallback(
    (view: { posAtDOM: (element: Element, offset: number) => number }, event: MouseEvent) => {
      if (!hasEditingPermissions) return false;

      // Try to handle tag context menu first
      const tagHandled = handleTagContextMenu(event, view);

      if (tagHandled) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      // For non-tag areas, fall back to browser native context menu
      return false;
    },
    [hasEditingPermissions, handleTagContextMenu],
  );

  const handleEditorSelectionUpdate = useCallback((editor: Editor) => {
    const { view, state } = editor;
    const { selection, doc } = state;
    const { from, to } = selection;

    // Find all tag elements and update their selection class
    const tagElements = view.dom.querySelectorAll(`[${EDITOR_DATA_ATTR.TAG_KEY}]`);
    tagElements.forEach((tagElement) => {
      // Get the position of this tag in the document
      const pos = view.posAtDOM(tagElement, 0);
      if (pos === -1) return;

      // Find the node at this position to get its size
      const resolvedPos = doc.resolve(pos);
      const node = resolvedPos.nodeAfter;
      if (!node || node.type.name !== VARIABLE_TAG_NAME) return;

      const tagEnd = pos + node.nodeSize;

      // Check if the tag is within the selection range
      // Tag is selected if selection overlaps with it (not just touches)
      const isInSelection = from < tagEnd && to > pos;

      if (isInSelection) {
        tagElement.classList.add(CSS_CLASSES.TAG_SELECTED);
      } else {
        tagElement.classList.remove(CSS_CLASSES.TAG_SELECTED);
      }
    });
  }, []);

  const handleEditorUpdate = useCallback(
    (editor: Editor) => {
      const newText = extractTextFromEditor(editor);
      if (newText !== lastValueRef.current) {
        lastValueRef.current = newText;
        onChange(newText);
      }
    },
    [onChange, lastValueRef],
  );

  return {
    contextMenu,
    handleSetContextMenu,
    handleEditorKeyDown,
    handleEditorPaste,
    handleEditorDOMKeyDown,
    handleEditorContextMenu,
    handleEditorSelectionUpdate,
    handleEditorUpdate,
  };
};
